import React, { useReducer } from 'react';
import { produce }           from 'immer';
import { KeyedObject }       from '../types';

enum ACTIONS {
	SET_CONTENT = 'setContent',
	SET_PROPS   = 'setProps',
	TOGGLE      = 'toggle',
	SHOW        = 'show',
}

export interface TogglableContentAction {
	type: ACTIONS,
	value: any,
	props?: unknown,
}

export interface TogglableContentState<P> {
	isOpen?: boolean,
	content?: any,
	props?: P | KeyedObject,
}

export interface TogglableContentHookActions<P> {
	setContent: TogglableContentSetFunction,
	setProps: TogglableContentPropsFunction<P>,
	toggle: TogglableContentToggleFunction,
	show: TogglableContentShowFunction<P>
}

export type TogglableContentSetFunction = ( content ?: any ) => void;
export type TogglableContentToggleFunction = ( isOpen ?: boolean ) => void;
export type TogglableContentPropsFunction<P> = ( propSet ?: P ) => void;
export type TogglableContentShowFunction<P> = ( content ?: any, toggleContainerOptions?: P ) => void;

export type TogglableContentHook<P = KeyedObject> = [ TogglableContentState<P>, TogglableContentHookActions<P> ];

const actionReducer = function reducer<P = KeyedObject>( state: TogglableContentState<P>, action: TogglableContentAction ){
	
	switch( action.type ){
		
		case ACTIONS.SET_CONTENT:
			return produce( state, draft => {
				draft.content = action.value
			} );
		
		case ACTIONS.SET_PROPS:
			return produce( state, draft => {
				draft.props = action.value as Partial<P>
			} );
		
		case ACTIONS.TOGGLE:
			return produce( state, draft => {
				draft.isOpen = ( action.value === true )
			} );
		
		case ACTIONS.SHOW:
			return produce( state, draft => {
				draft.content = action.value;
				draft.props   = action.props as Partial<P>;
				draft.isOpen  = true;
			} );
		default:
			throw new Error();
	}
};

export const useTogglableContentReducer = function <P = KeyedObject>( initialState = {} ): TogglableContentHook<P>{
	
	let state: TogglableContentState<P> = {
		isOpen:  false,
		content: null,
		props:   ( {} as P ),
		...initialState
	};
	
	const [ _state, _dispatch ] = useReducer( actionReducer, state, undefined );
	
	let actions: TogglableContentHookActions<P> = {
		'setContent': ( value = null ) => _dispatch( { type: ACTIONS.SET_CONTENT, value } ),
		'setProps':   ( ( value = ( {} as P ) ) => _dispatch( { type: ACTIONS.SET_PROPS, value } ) ) as TogglableContentPropsFunction<P>,
		'toggle':     ( value ) => _dispatch( { type: ACTIONS.TOGGLE, value } ),
		'show':       ( ( value = null, props = ( {} as P ) ) => _dispatch( { type: ACTIONS.SHOW, value, props } ) ) as TogglableContentShowFunction<P>,
	}
	
	// [ TogglableContentState, TogglableContentHookActions ]
	return [ _state, actions ];
};
